import React, { useEffect, useState } from "react"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import dayjs from "dayjs"
import {
  Row,
  Col,
  theme as themeConfig,
  DatePicker,
  Tooltip,
  Button,
  Select,
  Switch,
  message,
  Dropdown,
  Menu,
  Segmented,
  Space,
  Table,
  Card,
} from "antd"
import {
  ReloadOutlined,
  CalendarOutlined,
  DashboardOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  LinkOutlined,
  PieChartOutlined,
  TableOutlined,
  MoneyCollectOutlined,
} from "@ant-design/icons"

import {
  fetchMetrics,
  fetchSessionData,
  fetchCampaignRecordsData,
  updateCampaignAnalyticsState,
  fetchGraphRefreshData,
} from "../../../../../data/redux/campaign_analytics_details/actions"

import { formatAndBindToJSONToDayjs } from "../../../../../data/utils/common"
import {
  CARD_IDS_BY_TYPE,
  FULL_WIDTH_GRAPH_IDS_BY_TYPE,
  GRAPH_IDS_BY_TYPE,
  FULL_WIDTH_TABLE_IDS_BY_TYPE,
  TABLE_IDS_BY_TYPE,
  QUICK_RANGES
} from "../components/Metrics/constants"
import { IMG_ERROR } from "../../../../../data/assets"

import ShowMessage from "../../../../components/ShowMessage"
import MetricsCard from "../components/Metrics/MetricsCard"

import MetricsGraphGroup from "./MetricsGraphGroup"
import GraphTableCard from "./GraphTableCard"

import "../index.scss"

const Metrics = ({
  metrics,
  graphs,
  actions,
  selectedCampaignIds,
  selectedDateRange,
  hLoadError,
  sLoadError,
  cLoadError,
  hLoading,
  sLoading,
  cLoading,
  tables,
}) => {
  const { token } = themeConfig.useToken()
  const {
    fetchMetrics,
    updateCampaignAnalyticsState,
    fetchSessionData,
    fetchCampaignRecordsData,
    fetchGraphRefreshData,
  } = actions
  const [refreshInterval, setRefreshInterval] = useState(5) // in minutes
  const [liveMode, setLiveMode] = useState(false)
  const [timeLeft, setTimeLeft] = useState(refreshInterval * 60)
  const [activeTab, setActiveTab] = useState("Overview")
  const [granularity, setGranularity] = useState("auto")

  useEffect(() => {
    const payload = {
      campaignIds: selectedCampaignIds,
      startDate: selectedDateRange[0].toJSON(),
      endDate: selectedDateRange[1].toJSON(),
      timezone: dayjs.tz.guess(),
      granularity,
    }
    if (liveMode) {
      payload.mode = "live"
      payload.interval = refreshInterval
    }
    fetchMetrics(payload)
    fetchSessionData(payload)
    fetchCampaignRecordsData(payload)
  }, [selectedCampaignIds])

  useEffect(() => {
    if (!liveMode) return

    let time = refreshInterval * 60
    let pendingCall = false
    setTimeLeft(time)

    const tick = () => {
      time -= 1
      setTimeLeft(time)

      if (time <= 0) {
        time = refreshInterval * 60

        if (document.visibilityState === "visible") {
          handleManualRefresh()
          pendingCall = false
        } else {
          pendingCall = true
        }
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (pendingCall) {
          handleManualRefresh()
          pendingCall = false
          time = refreshInterval * 60
          setTimeLeft(time)
        }
      }
    }

    const interval = setInterval(tick, 1000)
    document.addEventListener("visibilitychange", handleVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [liveMode, refreshInterval])

  useEffect(() => {
    if (!liveMode) return

    const [start, end] = selectedDateRange
    const duration = dayjs(end).diff(dayjs(start))

    if (duration > 60 * 60 * 1000) {
      const now = dayjs()
      const oneHourAgo = now.subtract(1, "hour")

      message.warning(
        "Live Mode supports a maximum of 1 hour of data. Adjusted the date range."
      )

      updateCampaignAnalyticsState({
        selectedDateRange: [oneHourAgo, now],
      })

      const payload = {
        campaignIds: selectedCampaignIds,
        startDate: oneHourAgo.toJSON(),
        endDate: now.toJSON(),
        timezone: dayjs.tz.guess(),
        mode: "live",
        interval: refreshInterval + "min",
        granularity,
      }

      fetchMetrics(payload)
      fetchSessionData(payload)
      fetchCampaignRecordsData(payload)
      return
    }

    const payload = {
      campaignIds: selectedCampaignIds,
      startDate: selectedDateRange[0].toJSON(),
      endDate: selectedDateRange[1].toJSON(),
      timezone: dayjs.tz.guess(),
      mode: "live",
      interval: refreshInterval + "min",
      granularity,
    }

    fetchMetrics(payload)
    fetchSessionData(payload)
    fetchCampaignRecordsData(payload)
  }, [liveMode])

  const handleGraphRefresh = (refreshData, callback) => {
    const payload = {
      id: refreshData.id,
      granularity: refreshData.granularity,
      filters: {
        startDate: refreshData.startDate?.toJSON(),
        endDate: refreshData.endDate?.toJSON(),
        campaignId: refreshData.campaignId,
      },
    }
    fetchGraphRefreshData(payload, callback)
  }

  const quickRangeMenu = (
    <Menu
      onClick={({ key }) => handleQuickRangeChange(key)}
      items={QUICK_RANGES.map(r => ({ key: r.value, label: r.label }))}
    />
  )

  const getErrorState = apiType => {
    switch (apiType) {
      case "sessionData":
        return sLoadError
      case "campaignsRecordsData":
        return cLoadError
      case "logsData":
      default:
        return hLoadError
    }
  }

  const getLoadingState = apiType => {
    switch (apiType) {
      case "sessionData":
        return sLoading
      case "campaignsRecordsData":
        return cLoading
      case "logsData":
      default:
        return hLoading
    }
  }

  const handleDateRangeChange = dates => {
    if (!dates || dates.length !== 2) {
      const payload = {
        campaignIds: selectedCampaignIds,
        granularity: granularity,
      }
      if (liveMode) {
        payload.mode = "live"
        payload.interval = refreshInterval + "min"
      }
      fetchMetrics(payload)
      return
    }

    const [start, end] = dates

    const formattedStart = formatAndBindToJSONToDayjs(
      start,
      "current"
    )?.toJSON()
    const formattedEnd = formatAndBindToJSONToDayjs(end, "current")?.toJSON()

    updateCampaignAnalyticsState({
      selectedDateRange: [start, end],
    })

    const payload = {
      startDate: formattedStart,
      endDate: formattedEnd,
      timezone: dayjs.tz.guess(),
      campaignIds: selectedCampaignIds,
      granularity,
    }
    if (liveMode) {
      payload.mode = "live"
      payload.interval = refreshInterval + "min"
    }
    fetchSessionData(payload)
    fetchMetrics(payload)
    fetchCampaignRecordsData(payload)
  }

  const renderCardById = id => {
    const component = metrics?.find(item => item?.id === id)
    return component ? (
      <MetricsCard
        key={id}
        item={component}
        loadError={getErrorState(component.apiType)}
        loading={getLoadingState(component.apiType)}
      />
    ) : null
  }

  const handleRetry = () => {
    const payload = {
      campaignIds: selectedCampaignIds,
      startDate: selectedDateRange[0]?.toJSON(),
      endDate: selectedDateRange[1]?.toJSON(),
      timezone: dayjs.tz.guess(),
      granularity: granularity,
    }
    if (liveMode) {
      payload.mode = "live"
      payload.interval = refreshInterval + "min"
    }
    fetchMetrics(payload)
  }

  const renderGraphSection = id => {
    const graph = graphs.find(g => g.id === id)
    if (!graph) return null

    return (
      <GraphTableCard
        graph={graph}
        loading={getLoadingState(graph.apiType)}
        loadError={getErrorState(graph.apiType)}
        handleRetry={handleRetry}
        onRefresh={handleGraphRefresh}
      />
    )
  }

  const renderTableById = id => {
    const table = tables.find(item => item.id === id)
    if (!table) return null

    return (
      <Card
        title={table.title || "Table Data"}
        className="metrics-table-card"
        size="small"
        bordered
        style={{
          boxShadow: token.boxShadowTertiary,
          border: `1px solid ${token.colorBorderSecondary}`,
        }}
        extra={
          <Tooltip title="Table View">
            <TableOutlined />
          </Tooltip>
        }
      >
        <Table
          columns={table.tableData?.columns || []}
          dataSource={table.tableData?.dataSource || []}
          pagination={false}
          size="small"
          bordered
          scroll={{ x: "max-content", y: 300 }}
          rowClassName={record => {
            if (!record.attributes) return ""

            switch (record.attributes.state.toLowerCase()) {
              case "success":
                return "status-success-row"
              case "warning":
                return "status-warning-row"
              case "error":
                return "status-error-row"
              default:
                return ""
            }
          }}
        />
      </Card>
    )
  }

  const handleManualRefresh = () => {
    // const intervalDuration = refreshInterval * 60 * 1000 // ms

    const [start, end] = selectedDateRange
    const dayjsEnd = dayjs() // current time set to end
    // const duration = end.diff(start)
    // if (duration > 3 * intervalDuration) {
    //   start = dayjs(start).add(refreshInterval, "minute")
    // } else if (duration > 60 * 60 * 1000) {
    //   start = end.subtract(1, "hour")
    // }
    const newStart = formatAndBindToJSONToDayjs(start, "current")
    const newEnd = formatAndBindToJSONToDayjs(dayjsEnd, "current")

    updateCampaignAnalyticsState({
      selectedDateRange: [newStart, newEnd],
    })

    const payload = {
      campaignIds: selectedCampaignIds,
      startDate: newStart.toJSON(),
      endDate: newEnd.toJSON(),
      timezone: dayjs.tz.guess(),
      granularity,
    }
    if (liveMode) {
      payload.mode = "live"
      payload.interval = refreshInterval + "min"
    }
    fetchSessionData(payload)
    fetchMetrics(payload)
    fetchCampaignRecordsData(payload)
  }

  const handleRefresh = () => {
    let [start, end] = selectedDateRange
    const newStart = formatAndBindToJSONToDayjs(start, "current")
    const dayjsEnd = dayjs()
    const newEnd = formatAndBindToJSONToDayjs(dayjsEnd, "current")

    updateCampaignAnalyticsState({
      selectedDateRange: [newStart, newEnd],
    })

    const payload = {
      campaignIds: selectedCampaignIds,
      startDate: newStart.toJSON(),
      endDate: newEnd.toJSON(),
      timezone: dayjs.tz.guess(),
      granularity,
    }
    if (liveMode) {
      payload.mode = "live"
      payload.interval = refreshInterval + "min"
    }
    fetchSessionData(payload)
    fetchMetrics(payload)
    fetchCampaignRecordsData(payload)
  }

  const handleQuickRangeChange = value => {
    let start, end
    const now = dayjs()
    switch (value) {
      case "today":
        start = now.startOf("day")
        end = now
        break
      case "week":
        start = now.startOf("week")
        end = now
        break
      case "15m":
        start = now.subtract(15, "minute")
        end = now
        break
      case "30m":
        start = now.subtract(30, "minute")
        end = now
        break
      case "1h":
        start = now.subtract(1, "hour")
        end = now
        break
      case "2h":
        start = now.subtract(2, "hour")
        end = now
        break
      case "3h":
        start = now.subtract(3, "hour")
        end = now
        break
      case "6h":
        start = now.subtract(6, "hour")
        end = now
        break
      case "12h":
        start = now.subtract(12, "hour")
        end = now
        break
      case "24h":
        start = now.subtract(24, "hour")
        end = now
        break
      case "7d":
        start = now.subtract(7, "day")
        end = now
        break
      default:
        return
    }
    const newStart = formatAndBindToJSONToDayjs(start, "current")
    const newEnd = formatAndBindToJSONToDayjs(end, "current")
    updateCampaignAnalyticsState({ selectedDateRange: [newStart, newEnd] })

    const payload = {
      campaignIds: selectedCampaignIds,
      startDate: newStart.toJSON(),
      endDate: newEnd.toJSON(),
      timezone: dayjs.tz.guess(),
      granularity,
    }
    if (liveMode) {
      payload.mode = "live"
      payload.interval = refreshInterval + "min"
    }
    fetchSessionData(payload)
    fetchMetrics(payload)
    fetchCampaignRecordsData(payload)
  }

  const handleGranularityChange = value => {
    updateCampaignAnalyticsState({ granularity: value })

    setGranularity(value)
    const payload = {
      campaignIds: selectedCampaignIds,
      startDate: selectedDateRange[0].toJSON(),
      endDate: selectedDateRange[1].toJSON(),
      timezone: dayjs.tz.guess(),
      granularity: value,
    }

    if (liveMode) {
      payload.mode = "live"
      payload.interval = refreshInterval + "min"
    }

    fetchSessionData(payload)
    fetchMetrics(payload)
    fetchCampaignRecordsData(payload)
  }

  const layoutConfig = [
    // {
    //   cards: [],
    //   graph: "",
    // },
  ]

  const getFilteredFullWidthGraphIds = () => {
    return FULL_WIDTH_GRAPH_IDS_BY_TYPE[activeTab] || []
  }

  const getFilteredCardIds = () => {
    return CARD_IDS_BY_TYPE[activeTab] || []
  }

  const getFilteredGraphIds = () => {
    return GRAPH_IDS_BY_TYPE[activeTab] || []
  }

  const getFilteredFullWidthTableIds = () => {
    return FULL_WIDTH_TABLE_IDS_BY_TYPE[activeTab] || []
  }

  const getFilteredTableIds = () => {
    return TABLE_IDS_BY_TYPE[activeTab] || []
  }

  return (
    <div
      style={{
        borderRadius: token.borderRadius,
      }}
    >
      {hLoadError && sLoadError && cLoadError ? (
        <ShowMessage
          ImgComponent={IMG_ERROR}
          title="Error occurred"
          message="Something went wrong while loading historic metrics. Please reload the page."
          height={200}
          width={200}
        />
      ) : (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: token.marginSM,
              flexWrap: "wrap",
            }}
          >
            <span
              style={{
                fontSize: token.fontSizeLG,
                fontWeight: token.fontWeightStrong,
                marginLeft: token.marginXXS,
              }}
            >
              Analytics Overview
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                marginBottom: token.marginXXS,
              }}
            >
              {/* Group 1: Live Mode Toggle & Interval */}
              <Space.Compact
                size="small"
                style={{
                  border: `1px solid ${token.colorBorderSecondary}`,
                  borderRadius: token.borderRadiusSM,
                }}
              >
                {liveMode && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      fontSize: token.fontSizeSM,
                      fontWeight: token.fontWeightStrong,
                      marginRight: token.marginSM,
                    }}
                  >
                    <span className="pulse-circle" />
                    <span
                      style={{
                        fontSize: token.fontSize,
                        fontWeight: token.fontWeight,
                        color:
                          timeLeft <= 30 ? token.colorError : token.colorText,
                      }}
                    >
                      {`${String(Math.floor(timeLeft / 60)).padStart(
                        2,
                        "0"
                      )}:${String(timeLeft % 60).padStart(2, "0")}`}
                    </span>
                  </div>
                )}

                <Tooltip title="Enable/disable live mode">
                  <Switch
                    checkedChildren="Live Mode"
                    unCheckedChildren="Live Mode"
                    checked={liveMode}
                    onChange={setLiveMode}
                    style={{
                      borderRight: `1px solid ${token.colorBorderSecondary}`,
                      marginRight: token.marginSM,
                    }}
                  />
                </Tooltip>

                <Tooltip title="Live mode interval">
                  <Select
                    value={refreshInterval}
                    onChange={setRefreshInterval}
                    style={{ width: 80 }}
                    size="small"
                    options={[
                      { value: 1, label: "1 min" },
                      { value: 5, label: "5 min" },
                      { value: 10, label: "10 min" },
                      { value: 15, label: "15 min" },
                      { value: 30, label: "30 min" },
                    ]}
                  />
                </Tooltip>
              </Space.Compact>

              {/* Group 2: Granularity & Refresh */}
              <Space.Compact
                size="small"
                style={{
                  border: `1px solid ${token.colorBorderSecondary}`,
                  borderRadius: token.borderRadiusSM,
                }}
              >
                <Tooltip title="Granularity">
                  <Select
                    value={granularity}
                    onChange={handleGranularityChange}
                    style={{ width: 100 }}
                    size="small"
                    options={[
                      { value: "auto", label: "Auto" },
                      { value: "minutes", label: "Minutes" },
                      { value: "hours", label: "Hours" },
                      { value: "days", label: "Days" },
                      { value: "weeks", label: "Weeks" },
                      { value: "months", label: "Months" },
                    ]}
                  />
                </Tooltip>

                <Tooltip title="Refresh">
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleRefresh}
                    size="small"
                  />
                </Tooltip>
              </Space.Compact>

              {/* Group 3: Date Range Controls */}
              <Space.Compact
                size="small"
                style={{
                  border: `1px solid ${token.colorBorderSecondary}`,
                  borderRadius: token.borderRadiusSM,
                }}
              >
                <Dropdown
                  overlay={quickRangeMenu}
                  trigger={["click"]}
                  placement="bottomLeft"
                >
                  <Tooltip title="Quick Select">
                    <Button
                      icon={<CalendarOutlined />}
                      size="small"
                      style={{
                        padding: 0,
                        width: 28,
                        height: 25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    />
                  </Tooltip>
                </Dropdown>

                <DatePicker.RangePicker
                  size="small"
                  value={selectedDateRange}
                  onChange={handleDateRangeChange}
                  showTime={{ format: "HH:mm" }}
                  format="YYYY-MM-DD HH:mm"
                  allowClear={false}
                  style={{ minWidth: 260 }}
                  disabled={liveMode}
                />
              </Space.Compact>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: token.marginXXS,
            }}
          >
            <Segmented
              options={[
                {
                  label: "Overview",
                  value: "Overview",
                  icon: <DashboardOutlined />,
                },
                {
                  label: "Call Duration",
                  value: "AHT",
                  icon: <ClockCircleOutlined />,
                },
                {
                  label: "Latency",
                  value: "Latency",
                  icon: <ThunderboltOutlined />,
                },
                {
                  label: "Connectivity",
                  value: "Connectivity",
                  icon: <LinkOutlined />,
                },
                {
                  label: "Disposition",
                  value: "Disposition",
                  icon: <PieChartOutlined />,
                },
                {
                  label: "Usage & Billing",
                  value: "UsageBilling",
                  icon: <MoneyCollectOutlined />,
                },
              ]}
              value={activeTab}
              onChange={setActiveTab}
              size="large"
              className="custom-segmented-tabs"
              style={{
                marginBottom: token.marginSM,
                borderRadius: token.borderRadiusLG,
                borderBottom: `2px solid ${token.colorTextQuaternary}`,
                // boxShadow: token.boxShadowTertiary,
                padding: 0,
              }}
            />
          </div>

          {/* Metrics Cards */}
          <Row gutter={[10, 10]}>
            {getFilteredCardIds().map(id => {
              const card = renderCardById(id)
              return card ? (
                <Col xs={24} sm={24} md={12} lg={8} xl={6} key={id}>
                  {card}
                </Col>
              ) : null
            })}
          </Row>

          {/* Metrics + Graphs */}
          {layoutConfig.length > 0 &&
            layoutConfig?.map((section, index) => (
              <MetricsGraphGroup
                key={index}
                cards={section.cards}
                graph={section.graph}
                metrics={metrics}
                graphs={graphs}
              />
            ))}

          {/* Graphs */}
          <Row gutter={[12, 12]} style={{ marginTop: token.margin }}>
            {getFilteredGraphIds().map(id => {
              const graph = renderGraphSection(id)
              if (!graph) return null
              return getFilteredFullWidthGraphIds().includes(id) ? (
                <Col xs={24} key={id}>
                  {graph}
                </Col>
              ) : (
                <Col xs={24} sm={24} md={12} key={id}>
                  {graph}
                </Col>
              )
            })}
          </Row>

          {/* Tables */}
          <Row gutter={[12, 12]} style={{ marginTop: token.margin }}>
            {getFilteredTableIds().map(id => {
              const table = renderTableById(id)
              if (!table) return null
              return getFilteredFullWidthTableIds().includes(id) ? (
                <Col xs={24} key={id}>
                  {table}
                </Col>
              ) : (
                <Col xs={24} sm={24} md={12} key={id}>
                  {table}
                </Col>
              )
            })}
          </Row>
        </>
      )}
    </div>
  )
}

const mapStateToProps = state => ({
  hLoadError: state.campaigns_analytics_details.hLoadError,
  sLoadError: state.campaigns_analytics_details.sLoadError,
  cLoadError: state.campaigns_analytics_details.cLoadError,
  hLoading: state.campaigns_analytics_details.hLoading,
  sLoading: state.campaigns_analytics_details.sLoading,
  cLoading: state.campaigns_analytics_details.cLoading,
  loading: state.campaigns_analytics_details.historicMetrics.loading,
  metrics: state.campaigns_analytics_details.historicMetrics.metrics,
  graphs: state.campaigns_analytics_details.historicMetrics.graphs,
  tables: state.campaigns_analytics_details.historicMetrics.tables,
  selectedCampaignIds: state.campaigns_analytics_details.selectedCampaignIds,
  selectedDateRange: state.campaigns_analytics_details.selectedDateRange,
})

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchMetrics,
      updateCampaignAnalyticsState,
      fetchSessionData,
      fetchCampaignRecordsData,
      fetchGraphRefreshData,
    },
    dispatch
  ),
})

export default connect(mapStateToProps, mapDispatchToProps)(Metrics)
